/**
 * CatalogTab.js — Configure items, availability, max qty per order
 * Includes AddItemForm and EditItemPanel sub-components
 * Extracted from AdminDashboard.js during Phase 1 refactor
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import S from "./adminStyles";
import { CATALOG_API } from "./adminConstants";
import { exportToCSV, printTable } from "../../utils/exportUtils";

// ── Add Item Form ───────────────────────────────────────────────────
function AddItemForm({ onAdd, onCancel, categories = [] }) {
  const [form, setForm] = useState({
    itemId: "",
    name: "",
    category: "other",
    krogerSku: "",
    caseQty: 1,
    unitCost: 0,
    maxQtyPerOrder: 3,
    primarySupplierId: "kroger",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div style={{ ...S.card, border: "2px solid #3b82f6" }}>
      <h3 style={{ fontSize: "14px", color: "#1e293b", margin: "0 0 12px 0" }}>
        Add New Catalog Item
      </h3>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}
      >
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Item ID (slug)
          </label>
          <input
            style={S.input}
            value={form.itemId}
            onChange={(e) =>
              set("itemId", e.target.value.toLowerCase().replace(/\s/g, "-"))
            }
            placeholder="black-beans-15oz"
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Display Name
          </label>
          <input
            style={S.input}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Black Beans (15 oz can)"
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Category
          </label>
          <select
            style={S.select}
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
          >
            <option value="">{"—"} Select category {"—"}</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
            <option value="other">Other (new category)</option>
          </select>
          {form.category === "other" && (
            <input
              style={{ ...S.input, marginTop: 6 }}
              placeholder="Enter new category name"
              value={form.customCategory || ""}
              onChange={(e) => set("customCategory", e.target.value)}
            />
          )}
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Primary Supplier
          </label>
          <select
            style={S.select}
            value={form.primarySupplierId}
            onChange={(e) => set("primarySupplierId", e.target.value)}
          >
            <option value="kroger">Kroger</option>
            <option value="costco">Costco</option>
            <option value="walmart">Walmart</option>
            <option value="donation">Community Donation</option>
          </select>
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Kroger SKU
          </label>
          <input
            style={S.input}
            value={form.krogerSku}
            onChange={(e) => set("krogerSku", e.target.value)}
            placeholder="SKU-BEANS-001"
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Case Qty
          </label>
          <input
            style={S.input}
            type="number"
            value={form.caseQty}
            onChange={(e) => set("caseQty", parseInt(e.target.value) || 1)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Max Qty Per Order
          </label>
          <input
            style={S.input}
            type="number"
            value={form.maxQtyPerOrder}
            onChange={(e) =>
              set("maxQtyPerOrder", parseInt(e.target.value) || 1)
            }
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Unit Cost ($)
          </label>
          <input
            style={S.input}
            type="number"
            step="0.01"
            value={form.unitCost}
            onChange={(e) => set("unitCost", parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button style={S.btnPrimary} onClick={() => {
          const { customCategory, ...rest } = form;
          onAdd(form.category === "other" ? { ...rest, category: customCategory } : rest);
        }}>
          Add Item
        </button>
        <button style={S.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Edit Item Panel ─────────────────────────────────────────────────
function EditItemPanel({ item, onSave, onCancel, onDelete, categories = [] }) {
  const itemCategory = item.category || "";
  const knownCategory = categories.includes(itemCategory);
  const [form, setForm] = useState({
    name: item.name || "",
    category: knownCategory ? itemCategory : "other",
    customCategory: knownCategory ? "" : itemCategory,
    maxQtyPerOrder: item.maxQtyPerOrder || 3,
    caseQty: item.caseQty || 1,
    unitCost: item.unitCost || 0,
    availableForCycle: item.availableForCycle !== false,
    inStock: item.inStock !== false,
    reorderThreshold: item.reorderThreshold || 0,
    source: item.source || "kroger",
    krogerSku: item.krogerSku || "",
    imageUrl: item.imageUrl || "",
    scanCodeUrl: item.scanCodeUrl || "",
    labelUrl: item.labelUrl || "",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div style={{ ...S.card, border: "2px solid #f59e0b" }}>
      <h3 style={{ fontSize: "14px", color: "#1e293b", margin: "0 0 12px 0" }}>
        Edit: {item.itemId}
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "12px",
        }}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>
            Display Name
          </label>
          <input
            style={S.input}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Black Beans (15 oz can)"
          />
        </div>
        <div>
          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>
            Category
          </label>
          <select
            style={S.select}
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
            <option value="other">Other (new category)</option>
          </select>
          {form.category === "other" && (
            <input
              style={{ ...S.input, marginTop: 6 }}
              placeholder="Enter new category name"
              value={form.customCategory || ""}
              onChange={(e) => set("customCategory", e.target.value)}
            />
          )}
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Max Qty Per Order
          </label>
          <input
            style={S.input}
            type="number"
            value={form.maxQtyPerOrder}
            onChange={(e) =>
              set("maxQtyPerOrder", parseInt(e.target.value) || 1)
            }
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Case Qty
          </label>
          <input
            style={S.input}
            type="number"
            value={form.caseQty}
            onChange={(e) => set("caseQty", parseInt(e.target.value) || 1)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Unit Cost ($)
          </label>
          <input
            style={S.input}
            type="number"
            step="0.01"
            value={form.unitCost}
            onChange={(e) => set("unitCost", parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Reorder Threshold
          </label>
          <input
            style={S.input}
            type="number"
            value={form.reorderThreshold}
            onChange={(e) =>
              set("reorderThreshold", parseInt(e.target.value) || 0)
            }
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Source
          </label>
          <select
            style={S.select}
            value={form.source}
            onChange={(e) => set("source", e.target.value)}
          >
            <option value="kroger">Kroger Only</option>
            <option value="donated">Donated Only</option>
            <option value="both">Both</option>
          </select>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            justifyContent: "center",
          }}
        >
          <label style={{ fontSize: "12px" }}>
            <input
              type="checkbox"
              checked={form.availableForCycle}
              onChange={(e) => set("availableForCycle", e.target.checked)}
            />{" "}
            Available for current cycle
          </label>
          <label style={{ fontSize: "12px" }}>
            <input
              type="checkbox"
              checked={form.inStock}
              onChange={(e) => set("inStock", e.target.checked)}
            />{" "}
            In stock
          </label>
        </div>
      </div>
        {/* D-8: Asset URL fields (SOW 3 Phase 1) — GCS paths, no upload UI yet */}
        <div style={{ gridColumn: "1 / -1", marginTop: "8px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "8px" }}>
            ASSET URLs <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional — paste GCS paths when available)</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            <div>
              <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>Image URL</label>
              <input
                style={S.input}
                value={form.imageUrl}
                onChange={(e) => set("imageUrl", e.target.value)}
                placeholder="gs://raider-depot-assets-prod/images/..."
              />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>Scan Code URL</label>
              <input
                style={S.input}
                value={form.scanCodeUrl}
                onChange={(e) => set("scanCodeUrl", e.target.value)}
                placeholder="gs://raider-depot-assets-prod/qr/..."
              />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>Label URL</label>
              <input
                style={S.input}
                value={form.labelUrl}
                onChange={(e) => set("labelUrl", e.target.value)}
                placeholder="gs://raider-depot-assets-prod/labels/..."
              />
            </div>
          </div>
        </div>
      <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          <button style={S.btnPrimary} onClick={() => {
            const { customCategory, ...rest } = form;
            onSave(form.category === "other" ? { ...rest, category: customCategory } : rest);
          }}>
            Save Changes
          </button>
          <button style={S.btnSecondary} onClick={onCancel}>
            Cancel
          </button>
        </div>
        {onDelete && (
          <button
            style={{ padding: "6px 14px", borderRadius: "4px", border: "1px solid #fca5a5", background: "#fee2e2", color: "#dc2626", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
            onClick={onDelete}
          >
            Delete Item
          </button>
        )}
      </div>
    </div>
  );
}

function ConfirmDeleteDialog({ itemName, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '400px',
        width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.15)'
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#1e293b' }}>Delete Catalog Item?</h3>
        <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569' }}>
          <strong>{itemName}</strong> will be hidden from all views. This can be reviewed later using "Show Deleted Items".
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: '13px' }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
            onClick={onConfirm}
          >
            Delete Item
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Catalog Tab ─────────────────────────────────────────────────────
export default function CatalogTab({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [hoveredEdit, setHoveredEdit] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // stores itemId to confirm
  const savedScrollY = useRef(0);
  const editFormRef = useRef(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${CATALOG_API}/admin${showDeleted ? '?showDeleted=true' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data.items || [];
        setItems(
          arr.map((item) => ({ ...item, itemId: item.itemId || item.id })),
        );
      }
    } catch (err) {
      console.error("Catalog load error:", err);
    } finally {
      setLoading(false);
    }
  }, [user, showDeleted]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const updateItem = async (itemId, updates) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${CATALOG_API}/${encodeURIComponent(itemId)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        let msg = `Save failed (${res.status})`;
        try { const e = await res.json(); if (e.error) msg += `: ${e.error}`; } catch (_) {}
        alert(msg);
        return;
      }
      loadCatalog();
      setEditItem(null);
      setTimeout(() => { window.scrollTo({ top: savedScrollY.current, behavior: 'smooth' }) }, 50);
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const addItem = async (newItem) => {
    try {
      const token = await user.getIdToken();
      await fetch(`/api/catalog`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newItem),
      });
      loadCatalog();
      setShowAddForm(false);
    } catch (err) {
      console.error("Add error:", err);
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`/api/catalog/${encodeURIComponent(itemId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        alert(`Delete failed: ${err.error}`);
        return;
      }
      setEditItem(null);
      loadCatalog();
      setTimeout(() => { window.scrollTo({ top: savedScrollY.current, behavior: 'smooth' }) }, 50);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const restoreItem = async (itemId) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`/api/catalog/${encodeURIComponent(itemId)}/restore`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        alert(`Restore failed: ${err.error}`);
        return;
      }
      loadCatalog();
    } catch (err) {
      console.error('Restore error:', err);
    }
  };

  const handleCancelAdd = useCallback(() => setShowAddForm(false), []);
  const handleCancelEdit = useCallback(() => {
    setEditItem(null);
    setTimeout(() => { window.scrollTo({ top: savedScrollY.current, behavior: 'smooth' }) }, 50);
  }, []);

  useEffect(() => {
    if (!showAddForm) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCancelAdd();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showAddForm, handleCancelAdd]);

  useEffect(() => {
    if (!editItem) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCancelEdit();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editItem, handleCancelEdit]);

  const isSearching = searchQuery.trim().length > 0;
  const q = searchQuery.trim().toLowerCase();

  const sortedCategories = [
    ...new Set(items.map((i) => i.category || "Other")),
  ].sort();

  const itemsByCategory = Object.fromEntries(
    sortedCategories.map((cat) => [
      cat,
      items
        .filter((i) => (i.category || "Other") === cat)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    ]),
  );

  const filteredItems = isSearching
    ? items
        .filter(
          (i) =>
            (i.name || "").toLowerCase().includes(q) ||
            (i.itemId || "").toLowerCase().includes(q) ||
            (i.category || "").toLowerCase().includes(q),
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    : [];

  const toggleCategory = (cat) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const expandAll = () => setCollapsedCategories(new Set());
  const collapseAll = () => setCollapsedCategories(new Set(sortedCategories));

  const CATALOG_COLS = [
    { key: "itemId", label: "Item ID" },
    { key: "name", label: "Name" },
    { key: "category", label: "Category" },
    { key: "supplier", label: "Supplier" },
    { key: "krogerSku", label: "Kroger SKU" },
    { key: "caseQty", label: "Case Qty" },
    { key: "maxQtyPerOrder", label: "Max Per Order" },
    { key: "inStock", label: "In Stock" },
    { key: "availableForCycle", label: "Available for Cycle" },
  ];

  const catalogRows = () =>
    items.map((item) => ({ ...item, supplier: item.primarySupplierId || item.source }));

  const exportCatalogCSV = () => {
    const today = new Date().toISOString().split("T")[0];
    exportToCSV(`raider-depot-catalog-${today}.csv`, catalogRows(), CATALOG_COLS);
  };

  const printCatalog = () => {
    printTable("Catalog", `${items.length} items`, CATALOG_COLS, catalogRows());
  };

  const renderItemRow = (item) => (
    <tr
      key={item.itemId}
      style={item.deleted === true ? { backgroundColor: '#f1f5f9', opacity: 0.6 } : item.inStock === false ? { backgroundColor: "#fff0f0" } : undefined}
    >
      <td style={{ ...S.td, textAlign: "left" }}>
        <strong>{item.name}</strong>
        {item.inStock === false && (
          <span style={{ marginLeft: "8px", fontSize: "11px", color: "#dc2626", fontWeight: "600" }}>
            Out of Stock
          </span>
        )}
        <br />
        <span style={{ fontSize: "11px", color: "#94a3b8" }}>{item.itemId}</span>
      </td>
      <td style={{ ...S.td, textAlign: "left", whiteSpace: "nowrap" }}>{item.category || "—"}</td>
      <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>
        <span
          style={S.badge(
            item.primarySupplierId === "costco"
              ? "#f59e0b"
              : item.primarySupplierId === "donation"
                ? "#8b5cf6"
                : "#3b82f6",
          )}
        >
          {item.primarySupplierId || "kroger"}
        </span>
      </td>
      <td style={{ ...S.td, textAlign: "left", whiteSpace: "nowrap" }}>{item.krogerSku || "—"}</td>
      <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>{item.caseQty || "—"}</td>
      <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>{item.maxQtyPerOrder || "—"}</td>
      <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>
        <span style={S.badge(item.availableForCycle !== false ? "#16a34a" : "#dc2626")}>
          {item.availableForCycle !== false ? "Yes" : "No"}
        </span>
      </td>
      <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          {item.deleted !== true && (
            <button
              style={editItem === item.itemId
                ? { ...S.btnSecondary, padding: "4px 10px", fontSize: "12px" }
                : {
                    background: hoveredEdit === item.itemId ? "#2563EB" : "#1A3B6E",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                    padding: "5px 12px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                  }}
              onMouseEnter={() => setHoveredEdit(item.itemId)}
              onMouseLeave={() => setHoveredEdit(null)}
              onClick={() => {
                if (editItem !== item.itemId) {
                  savedScrollY.current = window.scrollY;
                  window.scrollTo(0, 0);
                  setEditItem(item.itemId);
                  setTimeout(() => { editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }, 50);
                } else {
                  setEditItem(null);
                }
              }}
            >
              {editItem === item.itemId ? "Close" : "Edit"}
            </button>
          )}
          {item.deleted === true && (
            <button
              style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              onClick={() => restoreItem(item.itemId)}
            >
              Restore
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <>
      {/* ── Single sticky wrapper: toolbar + column headers ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ffffff", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        <div style={{ padding: "12px 20px 8px" }}>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 16px", backgroundColor: "#f8fafc", marginBottom: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <input
                style={{ ...S.input, margin: 0, width: "280px" }}
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#6b7280", fontSize: "16px", cursor: "pointer", lineHeight: 1, padding: 0 }}
                  onClick={() => setSearchQuery("")}
                >
                  &#x2715;
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                Show Deleted Items
              </label>
              <button style={S.btnSecondary} onClick={exportCatalogCSV} disabled={items.length === 0}>
                Export CSV
              </button>
              <button style={S.btnSecondary} onClick={printCatalog} disabled={items.length === 0}>
                Print
              </button>
              <button
                style={S.btnPrimary}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? "Cancel" : "+ Add Item"}
              </button>
            </div>
          </div>
        </div>
        <div style={{ padding: "0 20px" }}>
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "auto" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "90px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "80px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "80px" }} />
              <col style={{ width: "80px" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...S.th, textAlign: "left" }}>Item</th>
                <th style={{ ...S.th, textAlign: "left", whiteSpace: "nowrap" }}>Category</th>
                <th style={{ ...S.th, textAlign: "center", whiteSpace: "nowrap" }}>Supplier</th>
                <th style={{ ...S.th, textAlign: "center", whiteSpace: "normal" }}>Supplier<br />SKU/UPC</th>
                <th style={{ ...S.th, textAlign: "center", whiteSpace: "nowrap" }}>Case Qty</th>
                <th style={{ ...S.th, textAlign: "center", whiteSpace: "normal" }}>Max Per<br />Order</th>
                <th style={{ ...S.th, textAlign: "center", whiteSpace: "nowrap" }}>Available</th>
                <th style={{ ...S.th, textAlign: "center", whiteSpace: "nowrap" }}>Actions</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      {showAddForm && (
        <AddItemForm
          onAdd={addItem}
          onCancel={handleCancelAdd}
          categories={sortedCategories}
        />
      )}

      {editItem && (
        <div ref={editFormRef}>
          <EditItemPanel
            item={items.find((i) => i.itemId === editItem)}
            categories={sortedCategories}
            onSave={(updates) => updateItem(editItem, updates)}
            onCancel={handleCancelEdit}
            onDelete={() => setConfirmDelete(editItem)}
          />
        </div>
      )}

      <div style={S.card}>
        {loading ? (
          <p style={{ color: "#64748b" }}>Loading catalog...</p>
        ) : (
          <>
            {!isSearching && (
              <div style={{ marginBottom: "8px", textAlign: "right", fontSize: "12px" }}>
                <button
                  style={{ background: "none", border: "none", padding: 0, fontSize: "12px", ...(collapsedCategories.size === 0 ? { color: "#9ca3af", cursor: "default", pointerEvents: "none" } : { color: "#2563EB", cursor: "pointer", fontWeight: 600 }) }}
                  onClick={expandAll}
                >
                  Expand All
                </button>
                {" | "}
                <button
                  style={{ background: "none", border: "none", padding: 0, fontSize: "12px", ...(collapsedCategories.size === sortedCategories.length ? { color: "#9ca3af", cursor: "default", pointerEvents: "none" } : { color: "#2563EB", cursor: "pointer", fontWeight: 600 }) }}
                  onClick={collapseAll}
                >
                  Collapse All
                </button>
              </div>
            )}
            <table style={{ ...S.table, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "auto" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "90px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "80px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "80px" }} />
                <col style={{ width: "80px" }} />
              </colgroup>
              <tbody>
                {isSearching ? (
                  filteredItems.length > 0 ? (
                    filteredItems.map(renderItemRow)
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ ...S.td, textAlign: "center", color: "#94a3b8" }}
                      >
                        No items match your search.
                      </td>
                    </tr>
                  )
                ) : (
                  sortedCategories.map((cat) => {
                    const catItems = itemsByCategory[cat];
                    const collapsed = collapsedCategories.has(cat);
                    return (
                      <React.Fragment key={cat}>
                        <tr
                          onClick={() => toggleCategory(cat)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: "#f1f5f9",
                            userSelect: "none",
                          }}
                        >
                          <td
                            colSpan={8}
                            style={{
                              padding: "8px 12px",
                              fontWeight: "600",
                              fontSize: "13px",
                              color: "#1e293b",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            {collapsed ? "▶" : "▼"} {cat} ({catItems.length} item{catItems.length !== 1 ? "s" : ""})
                          </td>
                        </tr>
                        {!collapsed && catItems.map(renderItemRow)}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDeleteDialog
          itemName={items.find(i => i.itemId === confirmDelete)?.name || confirmDelete}
          onConfirm={() => {
            deleteItem(confirmDelete);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}
